/* eslint-disable sonarjs/cognitive-complexity */
import type { CosmicProductType } from '@/lib/cms/cosmic/types/CosmicProductType'
import { getProductData } from '@/lib/cms/getProductData'
import { jeeves } from '@/lib/services/Jeeves'
import { assignPricingToVariation } from '@/lib/utils/api/assignPricingToVariation'
import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState } from 'react'

type Props = {
  recommendedProductsStringArray?: string[]
}

export const useGetProductRecommendations = ({
  recommendedProductsStringArray,
}: Props) => {
  const {
    cart: { otp, subscribe },
    app: { locale, alpha2 },
  } = useGlobalStore()

  const [recommendedProducts, setRecommendedProducts] = useState<
    CosmicProductType[] | null
  >(null)
  const [isLoading, setIsLoading] = useState(true)

  const getRecommendations = async () => {
    setIsLoading(true)

    const cartRecommendedProductsFlat = [
      ...otp.flatMap((item) => item.recommendedProducts ?? []),
      ...subscribe.flatMap((item) => item.recommendedProducts ?? []),
    ]

    // If passed a recommendedProducts prop, that will be used, else will look at items in cart
    const recommendedProductsFlat = [
      ...(recommendedProductsStringArray || []),
      ...cartRecommendedProductsFlat,
    ]

    if (recommendedProductsFlat.length === 0) {
      setRecommendedProducts(null)
      setIsLoading(false)
      return
    }

    const noDuplicates = [...new Set(recommendedProductsFlat)]

    const withoutItemsInCart = noDuplicates.filter(
      (product) =>
        !otp.some((item) => item.slug === product) &&
        !subscribe.some((item) => item.slug === product),
    )

    const promises = withoutItemsInCart.map((slug) =>
      getProductData({ productSlug: slug, locale }),
    )

    const productDataResults = await Promise.allSettled(promises)

    const productsData = productDataResults.map((result) =>
      result.status === 'fulfilled' ? result.value : null,
    )

    const fetchedProducts = productsData.filter((product) => product !== null)

    const fetchedFilteredForAvailableVariations = fetchedProducts.filter(
      (product) =>
        product !== null &&
        !('error' in product) &&
        product.variations.length > 0 &&
        product.variations[0],
    )

    const allProductSkus: string[] = fetchedFilteredForAvailableVariations
      .flatMap((product) => {
        if (!product) return 'error'
        if ('error' in product) return 'error'

        return product.variations
          ?.map((variation) => {
            const { sku } = variation

            return sku || null
          })
          .filter(
            (sku): sku is string =>
              typeof sku === 'string' && sku.trim() !== '',
          )
      })
      .filter((sku) => sku !== 'error')

    if (
      allProductSkus.length === 0 ||
      (allProductSkus.length === 1 && allProductSkus[0] === undefined)
    ) {
      setRecommendedProducts(null)
      setIsLoading(false)
      return
    }

    const pricingData = await jeeves.getProductPricing({
      market: alpha2,
      skus: allProductSkus,
    })

    if (!pricingData) {
      setRecommendedProducts(null)
      setIsLoading(false)
      return
    }

    const productsWithAssignedPricing: CosmicProductType[] =
      fetchedFilteredForAvailableVariations
        .map((product) =>
          product
            ? assignPricingToVariation({
                // @ts-expect-error: There isn't an error, TS thinks product could be null
                // it will never be null. In fact, vscode does not show the error, it only appears on the terminal.
                productData: product,
                pricingData,
              })
            : null,
        )
        .filter((product): product is CosmicProductType => product !== null)

    setRecommendedProducts(productsWithAssignedPricing)
    setIsLoading(false)
  }

  useEffect(() => {
    ;(async () => {
      await getRecommendations()
    })()
  }, [alpha2, otp, subscribe, locale])

  return { recommendedProducts, isLoading }
}
